import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';
import { BarChart3, DollarSign, Users, ArrowLeftRight, TrendingUp, Globe, Home, Loader2 } from 'lucide-react';
import { useCurrency, symFor } from '../hooks/useCurrency';

const AdminDashboard = () => {
  const { sym: mySym } = useCurrency();
  const [data, setData] = useState(null);
  const [recentTxns, setRecentTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [revRes, txnRes] = await Promise.all([
        api.get('/admin/revenue'),
        api.get('/admin/transactions?limit=20')
      ]);
      setData(revRes.data);
      setRecentTxns(txnRes.data.transactions);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Admin access required. You do not have permission to view this page.');
      } else {
        setError('Failed to load dashboard data');
      }
      toast.error(err.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-muted">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <p className="text-destructive text-lg font-medium" data-testid="admin-error">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted" data-testid="admin-dashboard-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-8">
          <BarChart3 className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold" style={{fontFamily: 'DM Sans'}} data-testid="admin-title">Revenue Dashboard</h1>
            <p className="text-sm text-muted-foreground">Platform-wide metrics and fee collection</p>
          </div>
        </div>

        {/* Top Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="border shadow-sm" data-testid="stat-total-fees">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">Total Fees Collected</p>
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <p className="text-3xl font-bold text-primary">{mySym}{data.total_fees_collected.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              <p className="text-xs text-muted-foreground mt-1">{data.total_fee_transactions} fee transactions</p>
            </CardContent>
          </Card>
          <Card className="border shadow-sm" data-testid="stat-total-volume">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">Transaction Volume</p>
                <TrendingUp className="h-5 w-5 text-green-500" />
              </div>
              <p className="text-3xl font-bold">{mySym}{data.total_transaction_volume.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              <p className="text-xs text-muted-foreground mt-1">{data.total_transactions} total transactions</p>
            </CardContent>
          </Card>
          <Card className="border shadow-sm" data-testid="stat-total-users">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">Registered Users</p>
                <Users className="h-5 w-5 text-blue-500" />
              </div>
              <p className="text-3xl font-bold">{data.total_users}</p>
              <p className="text-xs text-muted-foreground mt-1">Completed registrations</p>
            </CardContent>
          </Card>
          <Card className="border shadow-sm" data-testid="stat-avg-fee">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">Avg Fee / Transaction</p>
                <ArrowLeftRight className="h-5 w-5 text-amber-500" />
              </div>
              <p className="text-3xl font-bold">
                {mySym}{data.total_fee_transactions > 0 ? (data.total_fees_collected / data.total_fee_transactions).toFixed(2) : '0.00'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Per transfer</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Revenue by Currency */}
          <Card className="border shadow-sm" data-testid="revenue-by-currency">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Globe className="h-4 w-4 text-primary" /> Revenue by Currency</CardTitle>
            </CardHeader>
            <CardContent>
              {data.revenue_by_currency.length === 0 ? (
                <p className="text-sm text-muted-foreground">No fee data yet</p>
              ) : (
                <div className="space-y-3">
                  {data.revenue_by_currency.map((r, i) => (
                    <div key={r.currency} className="flex items-center justify-between" data-testid={`currency-row-${r.currency}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-primary/10 text-primary font-bold px-2 py-1 rounded">{r.currency}</span>
                        <span className="text-sm text-muted-foreground">{r.count} txns</span>
                      </div>
                      <span className="font-semibold">{symFor(r.currency)}{r.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Revenue by Transfer Type */}
          <Card className="border shadow-sm" data-testid="revenue-by-type">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><ArrowLeftRight className="h-4 w-4 text-primary" /> Domestic vs International</CardTitle>
            </CardHeader>
            <CardContent>
              {data.revenue_by_type.length === 0 ? (
                <p className="text-sm text-muted-foreground">No fee data yet</p>
              ) : (
                <div className="space-y-4">
                  {data.revenue_by_type.map((r, i) => (
                    <div key={r.type} data-testid={`type-row-${r.type}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="inline-flex items-center gap-1 text-sm font-medium capitalize">
                          {r.type === 'domestic' ? <Home className="h-3 w-3 text-green-600" /> : <Globe className="h-3 w-3 text-blue-600" />}
                          {r.type}
                        </span>
                        <span className="font-semibold">{mySym}{r.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-muted rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${r.type === 'domestic' ? 'bg-green-500' : 'bg-blue-500'}`}
                            style={{ width: `${Math.min(100, (r.total / Math.max(data.total_fees_collected, 1)) * 100)}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-muted-foreground">{r.count} txns</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Users by Currency */}
          <Card className="border shadow-sm" data-testid="users-by-currency">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Users by Currency</CardTitle>
            </CardHeader>
            <CardContent>
              {data.users_by_currency.length === 0 ? (
                <p className="text-sm text-muted-foreground">No users yet</p>
              ) : (
                <div className="space-y-3">
                  {data.users_by_currency.map((u, i) => (
                    <div key={u.currency} className="flex items-center justify-between" data-testid={`users-row-${u.currency}`}>
                      <span className="text-xs bg-muted font-bold px-2 py-1 rounded">{u.currency}</span>
                      <span className="font-semibold">{u.count} users</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Fee Transactions */}
        <Card className="border shadow-sm" data-testid="recent-fees-card">
          <CardHeader>
            <CardTitle className="text-lg">Recent Fee Collections</CardTitle>
            <CardDescription>Last 20 fee entries from transfers</CardDescription>
          </CardHeader>
          <CardContent>
            {data.recent_fees.length === 0 ? (
              <p className="text-sm text-muted-foreground">No fees collected yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 font-medium">Reference</th>
                      <th className="pb-2 font-medium">Type</th>
                      <th className="pb-2 font-medium">Currency</th>
                      <th className="pb-2 font-medium">Rate</th>
                      <th className="pb-2 font-medium text-right">Fee</th>
                      <th className="pb-2 font-medium text-right">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_fees.map((fee, i) => (
                      <tr key={fee.reference || i} className="border-b border-border/30" data-testid={`fee-row-${i}`}>
                        <td className="py-2 font-mono text-xs">{fee.reference}</td>
                        <td className="py-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${fee.transfer_type === 'domestic' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                            {fee.transfer_type}
                          </span>
                        </td>
                        <td className="py-2 font-medium">{fee.sender_currency}</td>
                        <td className="py-2">{fee.fee_percent_rate}% + {symFor(fee.sender_currency)}{fee.fee_flat_amount}</td>
                        <td className="py-2 text-right font-semibold text-primary">{symFor(fee.sender_currency)}{fee.fee_amount.toFixed(2)}</td>
                        <td className="py-2 text-right text-muted-foreground">{fee.created_at?.slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Transactions System-wide */}
        <Card className="border shadow-sm mt-6" data-testid="recent-transactions-card">
          <CardHeader>
            <CardTitle className="text-lg">Recent Transactions (All Users)</CardTitle>
            <CardDescription>Last 20 transactions across the platform</CardDescription>
          </CardHeader>
          <CardContent>
            {recentTxns.length === 0 ? (
              <p className="text-sm text-muted-foreground">No transactions yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="pb-2 font-medium">Reference</th>
                      <th className="pb-2 font-medium">Type</th>
                      <th className="pb-2 font-medium">Status</th>
                      <th className="pb-2 font-medium text-right">Amount</th>
                      <th className="pb-2 font-medium text-right">Fee</th>
                      <th className="pb-2 font-medium text-right">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTxns.map((txn, i) => (
                      <tr key={txn.reference || i} className="border-b border-border/30" data-testid={`txn-row-${i}`}>
                        <td className="py-2 font-mono text-xs">{txn.reference}</td>
                        <td className="py-2 capitalize">{txn.type?.replace('_', ' ')}</td>
                        <td className="py-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${txn.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {txn.status}
                          </span>
                        </td>
                        <td className={`py-2 text-right font-semibold ${txn.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {txn.amount > 0 ? '+' : ''}{txn.amount?.toFixed(2)}
                        </td>
                        <td className="py-2 text-right text-muted-foreground">{txn.fee_amount ? txn.fee_amount.toFixed(2) : '-'}</td>
                        <td className="py-2 text-right text-muted-foreground">{txn.created_at?.slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
